import * as React from "react"
import dayjs from "dayjs"
import { ChevronLeft, ChevronRight } from "@/components/icons"
import { cn } from "@/lib/utils"
import { hapticLight } from "@/lib/haptics"
import { DutyStatus } from "@@api/shared"

// Map of date string to array of DutyStatus entries for that day
export type DayStatusMap = Record<string, DutyStatus[]>

export interface RosterCalendarProps {
  month?: Date
  dayStatuses?: DayStatusMap
  selected?: Date
  onDayClick?: (date: Date) => void
  onMonthChange?: (date: Date) => void
  className?: string
}

const WEEKDAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"]

export function formatDateKey(date: Date | dayjs.Dayjs): string {
  return dayjs(date).format("YYYY-MM-DD")
}

export function RosterCalendar({
  month = new Date(),
  dayStatuses = {},
  selected,
  onDayClick,
  onMonthChange,
  className,
}: RosterCalendarProps) {
  const [currentMonth, setCurrentMonth] = React.useState(() => dayjs(month))

  React.useEffect(() => {
    setCurrentMonth(dayjs(month))
  }, [month])

  const daysInMonth = currentMonth.daysInMonth()
  const firstDayOfMonth = currentMonth.startOf("month").day()

  // Get days from previous month to fill the first row
  const prevMonth = currentMonth.subtract(1, "month")
  const prevMonthDays = prevMonth.daysInMonth()
  const prevMonthStartDay = prevMonthDays - firstDayOfMonth + 1

  // Calculate total cells needed (including next month days)
  const totalDays = firstDayOfMonth + daysInMonth
  const totalRows = Math.ceil(totalDays / 7)
  const totalCells = totalRows * 7

  const handlePrevMonth = () => {
    hapticLight()
    const newMonth = currentMonth.subtract(1, "month")
    setCurrentMonth(newMonth)
    onMonthChange?.(newMonth.toDate())
  }

  const handleNextMonth = () => {
    hapticLight()
    const newMonth = currentMonth.add(1, "month")
    setCurrentMonth(newMonth)
    onMonthChange?.(newMonth.toDate())
  }

  const getDateStatuses = (date: dayjs.Dayjs): DutyStatus[] => {
    const dateKey = formatDateKey(date)
    return dayStatuses[dateKey] || []
  }

  // Color mapping based on Figma design using CSS variables
  const STATUS_DOT_COLORS: Record<DutyStatus, string> = {
    [DutyStatus.OnDuty]: "bg-status-on-duty",
    [DutyStatus.Standby]: "bg-status-standby",
    [DutyStatus.Training]: "bg-status-training",
    [DutyStatus.OffDuty]: "bg-status-off-duty",
    [DutyStatus.Leave]: "bg-status-off-duty",
    [DutyStatus.Sick]: "bg-status-off-duty",
    [DutyStatus.Positioning]: "bg-status-positioning",
  }

  const getDayClassName = (
    isCurrentMonth: boolean,
    isSelected: boolean
  ) => {
    const baseClass =
      "flex flex-col items-center justify-center p-2 rounded gap-2 text-sm text-center transition-colors aspect-square"

    if (!isCurrentMonth) {
      return cn(baseClass, "bg-surface-L0 text-content-primary-bold opacity-50")
    }

    if (isSelected) {
      return cn(baseClass, "ring-2 ring-primary ring-inset bg-surface-L2")
    }

    return cn(baseClass, "bg-neutral-100 text-content-primary-bold")
  }

  const renderStatusDots = (statuses: DutyStatus[]) => {
    // Get unique statuses - only show one dot per status type
    const uniqueStatuses = Array.from(new Set(statuses))

    return (
      <div className="flex gap-1 items-start min-h-[5px]">
        {uniqueStatuses.map((status, index) => (
          <div
            key={index}
            className={cn("rounded-full size-[5px]", STATUS_DOT_COLORS[status])}
          />
        ))}
      </div>
    )
  }

  const renderDays = () => {
    const days: React.ReactNode[] = []

    for (let i = 0; i < totalCells; i++) {
      let dayNumber: number
      let isCurrentMonth: boolean
      let date: dayjs.Dayjs

      if (i < firstDayOfMonth) {
        // Previous month days
        dayNumber = prevMonthStartDay + i
        isCurrentMonth = false
        date = prevMonth.date(dayNumber)
      } else if (i < firstDayOfMonth + daysInMonth) {
        // Current month days
        dayNumber = i - firstDayOfMonth + 1
        isCurrentMonth = true
        date = currentMonth.date(dayNumber)
      } else {
        // Next month days
        dayNumber = i - firstDayOfMonth - daysInMonth + 1
        isCurrentMonth = false
        date = currentMonth.add(1, "month").date(dayNumber)
      }

      const statuses = getDateStatuses(date)
      const isSelected = selected ? dayjs(selected).isSame(date, "day") : false

      days.push(
        <button
          key={i}
          type="button"
          // onClick={() => onDayClick?.(date.toDate())}
          className={cn(
            "bg-neutral-100 font-normal",
            getDayClassName(isCurrentMonth, isSelected)
          )}
        >
          <span className="leading-normal">{dayNumber}</span>
          {renderStatusDots(statuses)}
        </button>
      )
    }

    return days
  }

  return (
    <div className={cn("flex flex-col gap-4 w-full max-w-md mx-auto", className)}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={handlePrevMonth}
          className="flex items-center justify-center min-h-8 min-w-8 p-[7px] rounded-lg border border-border-L2 shadow-sm bg-surface-L0 active:scale-95 transition-transform"
        >
          <ChevronLeft className="size-[15px]" />
        </button>

        <span className="text-sm font-medium">
          {currentMonth.format("MMMM YYYY")}
        </span>

        <button
          type="button"
          onClick={handleNextMonth}
          className="flex items-center justify-center min-h-8 min-w-8 p-[7px] rounded-lg border border-border-L2 shadow-sm bg-surface-L0 active:scale-95 transition-transform"
        >
          <ChevronRight className="size-[15px]" />
        </button>
      </div>

      {/* Calendar Grid */}
      <div className="flex flex-col gap-px">
        {/* Weekday headers */}
        <div className="grid grid-cols-7">
          {WEEKDAYS.map((day, index) => {
            const isCurrentWeekday = dayjs().day() === index
            return (
              <div
                key={day}
                className={cn(
                  "flex items-center justify-center py-2 text-xs tracking-[0.18px]",
                  isCurrentWeekday
                    ? "text-content-primary-bold font-medium"
                    : "text-content-primary-subtle"
                )}
              >
                {day}
              </div>
            )
          })}
        </div>

        {/* Days grid */}
        <div className="grid grid-cols-7 gap-px">{renderDays()}</div>
      </div>
    </div>
  )
}
